<template lang="html">
    <modal :open="open" :hero="{image: (destination && destination.image) ? destination.image : null}" no-padding
           @closeModal="closeWayFinder">

        <!-- Empty state       -->
        <Loader v-if="status === 'empty'"></Loader>

        <!-- No CMS content available -->
        <div v-else-if="status === 'destinationContentFailed'">
            <h3>Something went wrong</h3>
            <p style="margin-bottom: 50px">
                Whoops, something went wrong. We can not help you at the moment. Please try again later or contact us
                via chat
            </p>
            <Button
                    title="Close"
                    @click.native="closeWayFinder()"
            />
        </div>

        <!-- CMS content available -->
        <div v-else>
            <h3>{{destination.title}}</h3>
            <Columns
                    :columns="[{ label: 'from', text: 'Current<br> Location' }, { label: 'to', text: `${destination.address}<br> ${destination.city}` }]"
                    :icon="'arrow-right'"
            />

            <!-- Location loading state -->
            <Spinner v-if="status === 'routeLoading'"/>


            <!-- No GPS location -->
            <div v-else-if="status === 'gpsProblem'" style="border: solid black 2px; padding: 10px">
                <b>Unable to get your current location</b>
                <p v-if="gpsError === 'PERMISSION_DENIED'" style="margin-bottom: 30px; margin-top: 20px">
                    To help you navigate we need your current location. It seems that you have blocked our App from retrieving your location. Please check your settings.
                </p>
                <p v-if="gpsError === 'POSITION_UNAVAILABLE'" style="margin-bottom: 30px; margin-top: 20px">
                    To help you navigate we need your current location. We were not able retrieve this at the moment. Most likely because your device has no GPS signal at the moment. Please try again later
                </p>
                <p v-if="gpsError === 'TIMEOUT'" style="margin-bottom: 30px; margin-top: 20px">
                    To help you navigate we need your current location. We were not able retrieve this at the moment;
                </p>
                <ShareLocation v-if="gpsError === 'POSITION_UNAVAILABLE' || gpsError === 'TIMEOUT'" @shareLocation="retryFetchRoute()" @cancel="closeWayFinder()"></ShareLocation>
            </div>

            <!-- No route info state-->
            <div v-else-if="status === 'routeNotAvailable'">
                <b>No information available</b>
                <p style="margin-bottom: 30px; margin-top: 20px">
                    Sorry, we can not help you navigating to your destination at the moment.
                </p>
                <Button
                        title="Close"
                        @click.native="closeWayFinder()"
                />
            </div>

            <!-- Show result state-->
            <div v-else-if="status === 'complete'">
                <Button v-for="(travelOption, provider, index) in travelOptions"
                        :key="`travel_provider_${provider}`"
                        :title="`${travelOption.title}`"
                        :subtext="`Takes ${travelOption.estimateTimeHuman}`"
                        :link="travelOption.deeplinkUrl"
                        icon-right="location"
                        :button-background="index === 0 ? 'dark' : 'transparent'"
                        :button-color="index === 0 ? 'light' : 'dark'"
                        :class="{'border--dark': index !== 0}"
                        target="_blank"
                />
            </div>
        </div>
    </modal>

</template>

<script>
  import { EventBus, EVENTS } from '@/services/events/EventBus'
  import Modal from '@/components/modals/Modal'
  import ContentRenderer from '@/components/bard-renderer/ContentRenderer'
  import LocationContentService from '@/services/LocationContentService'
  import Columns from '@/components/partials/Columns'
  import Picture from '@/components/image-caption/ImageCaption'
  import geoLocationService from '@/services/GeoLocationService'
  import wayfindingservice from '@/services/WayfindingService'
  import Button from '@/components/partials/Button'
  import Spinner from '@/components/loader/Spinner'
  import Loader from '@/components/loader/Loader'
  import ShareLocation from '@/components/location-sharing/ShareLocation'

  const STATUSOPTIONS = {
    empty: 'empty',
    destinationContentAvailable: 'destinationContentAvailable',
    destinationContentFailed: 'destinationContentFailed',
    gpsProblem: 'gpsProblem',
    routeLoading: 'routeLoading',
    routeNotAvailable: 'routeNotAvailable',
    complete: 'complete'
  }

  export default {
    name: 'WayfinderModal',
    components: {
      Modal,
      ContentRenderer,
      Columns,
      Picture,
      Button,
      Spinner,
      Loader,
      ShareLocation
    },
    data () {
      return {
        open: false,
        destination: null,
        status: 'empty',
        travelOptions: {},
        gpsError: undefined
      }
    },
    methods: {
      closeWayFinder () {
        this.open = false
      },
      async openWayfinder (location) {
        this.open = true

        this.destination = null
        this.status = STATUSOPTIONS.empty

        /*
            Step 1: Get the content of the destination from the CMS
         */
        try {
          const locationResponse = await LocationContentService.getLocation(location.slug)
          this.destination = locationResponse.data
          this.status = STATUSOPTIONS.destinationContentAvailable
        } catch (err) {
          this.toDestinationContentFailedState()
          return false
        }

        this.fetchRoute(this.destination)
      },
      async getCurrentLocation () {
        this.gpsError = undefined

        /*
            For local testing
            return {
              latitude: 52.371794,
              longitude: 4.947085
            }
         */



        const result = await geoLocationService.getCurrentLocation().then(geoLocation => {
            return {
              latitude: geoLocation.coords.latitude,
              longitude: geoLocation.coords.longitude
            }
          }).catch(
            error => {
              console.log('getCurrentLocation error', error)
              this.status = STATUSOPTIONS.gpsProblem
              console.log('window.GeolocationPositionError', window.GeolocationPositionError)
              switch (error.code) {
                case window.GeolocationPositionError.PERMISSION_DENIED:
                 this.gpsError = 'PERMISSION_DENIED'
                return false
                case window.GeolocationPositionError.POSITION_UNAVAILABLE:
                  this.gpsError = 'POSITION_UNAVAILABLE'
               return false
                case window.GeolocationPositionError.TIMEOUT:
                  this.gpsError = 'TIMEOUT'
                  return false
                default:
                  this.gpsError = 'TIMEOUT'
              }

            return false
          }
        )

        return result
      },
      async retryFetchRoute () {
        this.status = STATUSOPTIONS.routeLoading
        const sleep = m => new Promise(r => setTimeout(r, m))
        await sleep(1000)
        return await this.fetchRoute(this.destination)
      },
      async fetchRoute (location) {
        console.log('Fetchroute....')
        this.status = STATUSOPTIONS.routeLoading
        const currentLocation = await this.getCurrentLocation()
        if (!currentLocation) {
          return false
        }

        this.status = STATUSOPTIONS.routeLoading

        this.travelOptions = {}
        let payload = {
          startLongitude: currentLocation.longitude,
          startLatitude: currentLocation.latitude,
          endLongitude: parseFloat(location.lng),
          endLatitude: parseFloat(location.lat)
        }


        try {
          const directions = await wayfindingservice.getTravelOptions(payload)
          this.travelOptions = directions
        // https://gist.github.com/diachedelic/0d60233dab3dcae3215da8a4dfdcd434
          this.status = STATUSOPTIONS.complete
        } catch (e) {
          this.status = STATUSOPTIONS.routeNotAvailable
          return false
        }



      },
      toDestinationContentFailedState () {
        this.destination = {
          image: {
            url: '/img/thishostel/scribble-whoops.gif'
          }
        }
        this.status = STATUSOPTIONS.destinationContentFailed
      }
    },
    mounted () {
      EventBus.$on(EVENTS.OpenWayfinder, this.openWayfinder)
    },
    destroyed () {
      EventBus.$off(EVENTS.OpenWayfinder, this.openWayfinder)
    }
  }
</script>

<style lang="scss" scoped>

</style>
